/**
 * Use Case: Fetch User Details
 * 
 * Orchestrates fetching user details from the user service.
 * Part of clean architecture: Presentation → UseCase → Service
 */

import { UserService } from '@/domain/services/user/userService';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export class FetchUserDetailsUseCase {
  private userService: UserService;

  constructor() {
    this.userService = UserService.getInstance();
  }

  /**
   * Execute the use case
   * @param userId - The Firebase ID of the user to fetch
   * @returns UserEntity with complete user data, or null if not found
   */
  async execute(userId: string): Promise<UserEntity | null> {
    return await this.userService.fetchUserDetails(userId);
  }
}

/**
 * Factory function to create a new FetchUserDetailsUseCase instance
 * Use this in presentation layer instead of singleton
 * 
 * @example
 * // In a hook
 * const useCase = createFetchUserDetailsUseCase();
 * const userEntity = await useCase.execute(userId);
 */
export const createFetchUserDetailsUseCase = (): FetchUserDetailsUseCase => {
  return new FetchUserDetailsUseCase();
};
